import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Calculator, ArrowRight } from "lucide-react";

/**
 * Super-Admin-only card on the dashboard summarising which plans
 * prospects have shown interest in via the public price-calculator
 * widget over the last N days (default 30).
 *
 * Reads `GET /api/admin/leads/stats/pricing-calculator?days=30` which
 * is gated by `require_platform_admin`. Renders nothing on
 * non-admin contexts (parent guards via `isPlatformAdmin`).
 *
 * Empty state is intentional: "Ingen priskalkulator-henvendelser
 * siste 30 dager." — calm, no chart, no skeleton noise.
 */
export default function PricingInterestCard({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/leads/stats/pricing-calculator?days=${days}`)
      .then((r) => { if (alive) { setData(r.data); setErr(null); } })
      .catch((e) => { if (alive) setErr(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  // Soft-fail: if the request errors (e.g. non-admin viewer) we just
  // don't render anything — better than a red error toast.
  if (err) return null;

  return (
    <Card className="rounded-2xl border-border" data-testid="pricing-interest-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Calculator className="w-4 h-4" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                Super Admin
              </p>
              <h3 className="font-heading text-base font-semibold leading-tight">
                Pakkeinteresse siste {days} dager
              </h3>
            </div>
          </div>
          <Link
            to="/admin/leads"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
            data-testid="pricing-interest-leads-link"
          >
            Forespørsler <ArrowRight className="w-3 h-3" strokeWidth={1.75} />
          </Link>
        </div>

        {loading ? (
          <div className="mt-5 grid grid-cols-3 gap-3" data-testid="pricing-interest-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 rounded-xl bg-primary/5 animate-pulse" />
            ))}
          </div>
        ) : !data || data.total === 0 ? (
          <p className="mt-5 text-sm text-muted-foreground" data-testid="pricing-interest-empty">
            Ingen priskalkulator-henvendelser siste {days} dager.
          </p>
        ) : (
          <>
            <dl className="mt-5 grid grid-cols-3 gap-2 sm:gap-3 tabular-nums">
              {[
                { id: "basic",   label: "Basic" },
                { id: "pro",     label: "Pro" },
                { id: "premium", label: "Premium" },
              ].map((p) => (
                <div
                  key={p.id}
                  className="rounded-xl border border-border bg-accent/30 px-3 py-3"
                  data-testid={`pricing-interest-cell-${p.id}`}
                >
                  <dt className="text-xs text-muted-foreground">{p.label}</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold">
                    {data.counts[p.id] ?? 0}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">
              {data.total} {data.total === 1 ? "henvendelse" : "henvendelser"} totalt fra priskalkulatoren.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
